import { ChartDataset } from 'chart.js';
import { HistoricalOccupancy } from 'types/CarTracking';
import { ComparePeriod, comparePeriodOptions, getAvgSinceTimestamp } from '../../utils';

export type { ComparePeriod };
export { comparePeriodOptions, getAvgSinceTimestamp };

const comparePeriodAverageLabel: Record<ComparePeriod, string> = {
    '4w': '4-Week',
    '3m': '3-Month',
    '6m': '6-Month',
    '12m': '12-Month',
};

const TODAY_COLOR = '#ff6712';
const PAST_AVG_COLOR = '#0090FF';

type TimeBucket = {
    minutes: number;
    today: number | null;
    pastAvg: number | null;
};

export type DailyTrendChartData = {
    labels: string[];
    datasets: Array<ChartDataset<'line'>>;
    peakTodayTimeLabel: string;
    latestPastAverage: number | null;
    currentPeriodPastAverage: number | null;
    yAxisMax: number;
    yAxisStep: number;
};

function getYAxisStep(maxValue: number) {
    if (maxValue > 80) return 20;
    if (maxValue > 30) return 10;
    if (maxValue > 15) return 5;
    if (maxValue > 8) return 2;
    return 1;
}

function formatHourLabel(totalMinutes: number, includeMinutes = false) {
    const hour = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const twelveHour = hour % 12 || 12;
    if (includeMinutes) {
        return `${twelveHour}:${String(minutes).padStart(2, '0')} ${suffix}`;
    }
    return `${twelveHour} ${suffix}`;
}

function getHourlyBuckets(datapoints: HistoricalOccupancy['datapoints']) {
    // historical_occupancy returns 10-minute HH:MM buckets; keep only whole-hour points for the chart.
    return Object.entries(datapoints)
        .map(([key, value]) => {
            const [hourPart, minutePart] = key.split(':');
            const minutes = Number(hourPart) * 60 + Number(minutePart);
            return {
                minutes,
                today: value.today_occupancy,
                pastAvg: value.past_avg_occupancy,
            };
        })
        .filter((bucket) => bucket.minutes % 60 === 0)
        .sort((a, b) => a.minutes - b.minutes);
}

function trimVisibleWindow(buckets: TimeBucket[]) {
    const first = buckets.findIndex((bucket) => bucket.today !== null || bucket.pastAvg !== null);
    if (first < 0) return buckets;

    let last = buckets.length - 1;
    while (last >= 0 && buckets[last].today === null && buckets[last].pastAvg === null) {
        last -= 1;
    }

    return buckets.slice(Math.max(0, first - 1), Math.min(buckets.length, last + 2));
}

export function getDailyTrendChartData(report: HistoricalOccupancy, compareLegendLabel: string): DailyTrendChartData {
    const buckets = trimVisibleWindow(getHourlyBuckets(report.datapoints));
    const labels = buckets.map((bucket) => formatHourLabel(bucket.minutes));
    const todayData = buckets.map((bucket) => bucket.today);
    const pastAverageData = buckets.map((bucket) => bucket.pastAvg);

    let peakTodayTimeLabel = '';
    let peakTodayValue = -1;
    let latestComparableBucket: TimeBucket | null = null;
    for (const bucket of buckets) {
        if (typeof bucket.today === 'number' && bucket.today >= peakTodayValue) {
            peakTodayValue = bucket.today;
            peakTodayTimeLabel = formatHourLabel(bucket.minutes, true);
        }
        if (typeof bucket.today === 'number') {
            latestComparableBucket = bucket;
        }
    }

    const latestPastAverage =
        [...pastAverageData].reverse().find((value): value is number => typeof value === 'number') ?? null;
    const currentPeriodPastAverage = latestComparableBucket?.pastAvg ?? null;
    const maxValue = Math.max(
        15,
        ...todayData.map((value) => value ?? 0),
        ...pastAverageData.map((value) => value ?? 0),
        peakTodayValue
    );

    const yAxisStep = getYAxisStep(maxValue);
    const yAxisMax = Math.ceil((maxValue + 10) / yAxisStep) * yAxisStep;

    return {
        labels,
        peakTodayTimeLabel,
        latestPastAverage,
        currentPeriodPastAverage,
        yAxisMax,
        yAxisStep,
        datasets: [
            {
                label: 'Today',
                data: todayData,
                borderColor: TODAY_COLOR,
                backgroundColor: 'rgba(255, 103, 18, 0.12)',
                pointBackgroundColor: TODAY_COLOR,
                pointRadius: 0,
                fill: true,
                tension: 0.3,
                borderWidth: 2.5,
            },
            {
                label: compareLegendLabel,
                data: pastAverageData,
                borderColor: PAST_AVG_COLOR,
                backgroundColor: PAST_AVG_COLOR,
                pointRadius: 0,
                fill: false,
                tension: 0.3,
                borderDash: [6, 4],
                borderWidth: 2.5,
            },
        ],
    };
}

export function getAveragePeakWeekday(timezone: string, currentDate = new Date()) {
    return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        timeZone: timezone,
    }).format(currentDate);
}

export function getComparePeriodLegend(comparePeriod: ComparePeriod, timezone: string, currentDate = new Date()) {
    const weekday = getAveragePeakWeekday(timezone, currentDate);
    return `${comparePeriodAverageLabel[comparePeriod]} Average ${weekday}`;
}

export function getCurrentOccupancyDelta(currentOccupancy: number, latestPastAverage: number | null) {
    if (latestPastAverage === null) return null;

    const diff = currentOccupancy - latestPastAverage;
    const absDiff = Math.abs(diff);
    if (diff === 0) {
        return {
            direction: 'flat' as const,
            value: 0,
            text: 'at average',
        };
    }
    if (diff > 0) {
        return {
            direction: 'up' as const,
            value: absDiff,
            text: `${absDiff} vs Average`,
        };
    }
    return {
        direction: 'down' as const,
        value: absDiff,
        text: `${absDiff} below Average`,
    };
}

export function hasRenderableData(report: HistoricalOccupancy) {
    return Object.values(report.datapoints).some(
        ({ today_occupancy, past_avg_occupancy }) => today_occupancy !== null || past_avg_occupancy !== null
    );
}
